import { NO_PERMISSION } from 'config/constants';
import axios from 'services/http';
import { baseUrl } from 'services/http/base';
import { isError, patchRequest } from 'services/http/patchRequest';
import { poster } from 'services/http/swrFetcher';
import useSWRImmutable from 'swr/immutable';

type ActivityId = Record<'activity_id', number | string>;

export type ActivityParams = ActivityId & {
  type?: CompetitionType | PredictType;
  referral_code?: string;
};

type LotteryCountParam = {
  activity_id: number;
  type?: 'REWARDS' | 'BLIND_BOX';
};

type LotteryAddressParam = {
  lottery_id: number;
  address: string;
  batch_id?: number;
};

type VoteActivityParams = ActivityId & {
  voting_reward_id: number;
  voting_point: string;
};

type DetailParams = ActivityId & {
  page_size: number;
  page_num: number;
};

type PointsVotingParams = ActivityId & {
  voting_reward_id: number;
  voting_point: string;
};

export type GroupOperateParams = {
  activity_id: number;
  group_id: number;
};

type ChangeGroupAvator = ActivityId & {
  img: string;
};

type ChangeGroupName = ActivityId & {
  group_name: string;
};
export enum CompetitionType {
  'COMPETITION' = 'competition',
  'EVENT' = 'event',
  'HAPPY' = 'happy',
  'MOSAIC' = 'mosaic',
  'SPACESHIP' = 'boat',
}

export enum PredictType {
  'SINGLE' = 'single',
  'MANY' = 'many',
}

type ReferralCodeByUidParams = {
  uid: string;
};

type RankParams = Record<'activity_id' | 'page_no' | 'page_size', number> & Record<'type', RankType>;

type GroupUserRankParams = ActivityId & {
  group_id: number;
  uid_or_nick_name?: string;
  page_size: number;
  page_num: number;
  type: RankType;
};

type GroupDetailParams = ActivityId & { group_id: number; type: RankType };
type GroupKickOutParams = ActivityId & { uid: number };
type GroupInviteStatusChangeParams = ActivityId & { invite_status: boolean };
type RankTeamParams = Omit<RankParams, 'page_no'> & Record<'page_num', number>;

type CreateTeamParams = {
  activity_id: number;
  group_name?: string;
  group_icon?: string;
};

type JoinTeamParams = {
  activity_id: number;
  group_id?: number;
};

type SearchTeamRankParams = {
  activity_id: number;
  search_name: string;
  type: RankType;
};

type LotteryPagingParams = {
  page_num: number;
  page_size: number;
  activity_id: number;
  number?: number;
};

export { isError };

const getActivityDetail = async <T>(url: string, params: ActivityParams, config?: object, server = false) => {
  try {
    const res = await axios.post<{ data: T }>(`${baseUrl}/${url}`, params, config);
    if (res.code === 200) {
      return res.data;
    }
    if (server && res.code === 301) {
      return NO_PERMISSION;
    }
    return null;
  } catch {
    return null;
  }
};

export const getRegularDetail = <T>(params: ActivityParams, config?: object, server = false) =>
  getActivityDetail<T>('uapi/v1/activity_center/detail', params, config, server);

export const acceptActivity = async <T>(params: ActivityParams) => {
  return axios.post<T>(`${baseUrl}/uapi/v1/activity_center/accept`, params);
};

export const quitActivity = async <T>(params: ActivityParams) => {
  return axios.post<T>(`${baseUrl}/uapi/v1/activity_center/quit`, params);
};

export const checkActivityConflict = async <T>(params: ActivityParams) => {
  return axios.post<T>(`${baseUrl}/uapi/v1/activity_center/accepted`, params);
};

export const getActivityList = patchRequest('uapi/v1/activity_center/list');

export const getCompetitionDetail = <T>(params: ActivityParams, config?: object, server = false) =>
  getActivityDetail<T>('uapi/v1/activity_center/competition/detail', params, config, server);

export const getCompetitionTeamDetail = <T>(params: ActivityParams, config?: object, server = false) =>
  getActivityDetail<T>('uapi/v1/activity_center/group_competition/detail', params, config, server);

export const getLotteryDetail = <T>(params: ActivityParams, config?: object, server = false) =>
  getActivityDetail<T>('uapi/v1/activity_center/random/detail', params, config, server);

export const getRebateDetail = <T>(params: ActivityParams, config?: object, server = false) =>
  getActivityDetail<T>('uapi/v1/activity_center/rebate/detail', params, config, server);

export const getBoxDetail = <T>(params: ActivityParams, config?: object, server = false) =>
  getActivityDetail<T>('uapi/v1/activity_center/blind_box/detail', params, config, server);

export const getCompetitionRank = patchRequest<RankParams>('uapi/v1/activity_center/competition/rank');
export const getCompetitionTeamRank = patchRequest<RankTeamParams>(
  'uapi/v1/activity_center/group_competition/team/rank'
);
export const getReferralCodeByUid = patchRequest<ReferralCodeByUidParams>('uapi/v1/referral/invitation_code/uid');
export const getReferralCode = patchRequest('uapi/v1/referral/invitation_code', 'get');

export const checkUserExclusion = async <T>(params: { scene: 1 | 2 }) => {
  return axios.post<T>(`${baseUrl}/uapi/v1/bonus/lottery/exclusion`, params);
};
export const getLotteryList = async <T>(params: { activity_id: number }) => {
  return axios.post<T>(`${baseUrl}/uapi/v1/bonus/lottery/list`, params);
};
export const requestLottery = async <T>(params: {
  activity_id: number;
  uid?: number;
  lottery_times?: number;
  type?: 'REWARDS' | 'BLIND_BOX';
}) => {
  return axios.post<T>(`${baseUrl}/uapi/v1/bonus/lottery`, params);
};

export const getLotteryPrize = patchRequest<{
  activity_id: number;
  type: 'REWARDS' | 'BLIND_BOX';
  page_num: number;
  page_size: number;
}>('uapi/v1/bonus/lottery/prize');

export function useLotteryDetails(params: { activity_id: number }) {
  const { data, mutate, error, isLoading } = useSWRImmutable([`${baseUrl}/uapi/v1/bonus/lottery/me`, params], poster);

  const lotteryDetails = data ? data : [];

  return {
    lotteryDetails,
    mutateLotteryDetails: mutate,
    isLoading,
    isError: error,
  };
}
export const getLotteryCount = async <T>(params: LotteryCountParam) => {
  return axios.post<T>(`${baseUrl}/uapi/v1/bonus/lottery/count`, params);
};
export function useLotteryCount(params: LotteryCountParam) {
  const { data, mutate, error, isLoading } = useSWRImmutable(
    [`${baseUrl}/uapi/v1/bonus/lottery/count`, params],
    poster
  );

  const lotteryCount = data ? data.lottery_count : 0;

  return {
    lotteryCount,
    mutateLotteryCount: mutate,
    isLoading,
    isError: error,
  };
}

export const lotteryAddress = patchRequest<LotteryAddressParam>('uapi/v1/bonus/lottery/address/edit');

export const getPredictDetail = <T>(params: ActivityParams, config?: object, server = false) =>
  getActivityDetail<T>('uapi/v1/activity_center/voting_draw/detail', params, config, server);

export const voteActivity = patchRequest<VoteActivityParams>('uapi/v1/activity_center/vote');
export const getPointDetail = patchRequest<DetailParams>('uapi/v1/activity_center/voting/point/detail');
export const getInvitedDetail = patchRequest<DetailParams>('uapi/v1/activity_center/invited/detail');
export const pointsVoting = patchRequest<PointsVotingParams>('uapi/v1/activity_center/vote');

export const userAutoJoinGroup = <T>(params: { activity_id: number; referral_code?: string }) => {
  return axios.post<T>(`${baseUrl}/uapi/v1/activity_center/group_competition/user_status_and_join`, params);
};

export const getGroupDetail = patchRequest<GroupDetailParams>(
  'uapi/v1/activity_center/group_competition/group_detail',
  'get'
);

export const getGroupUserRankData = patchRequest<GroupUserRankParams>(
  'uapi/v1/activity_center/group_competition/group_user_rank',
  'get'
);
export const groupKickOut = patchRequest<GroupKickOutParams>('uapi/v1/activity_center/group_competition/kick');
export const groupInviteStatusChange = patchRequest<GroupInviteStatusChangeParams>(
  'uapi/v1/activity_center/group_competition/change_invite_status'
);

export const groupDisbanded = patchRequest<GroupOperateParams>(
  'uapi/v1/activity_center/group_competition/team/disbanded'
);
export const groupQuit = patchRequest<GroupOperateParams>('uapi/v1/activity_center/group_competition/team/quit');

export const joinGroup = patchRequest<GroupOperateParams>('uapi/v1/activity_center/group_competition/join_group');
export const uploadProfile = async <T>(params: FormData) => {
  return axios.post<T>(`${baseUrl}/uapi/v1/uc/profile/upload`, params, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
};

export const changeGroupAvator = async <T>(params: ChangeGroupAvator) => {
  return axios.post<T>(`${baseUrl}/uapi/v1/activity_center/group_competition/change_group_img`, params);
};

export const changeGroupName = async <T>(params: ChangeGroupName) => {
  return axios.post<T>(`${baseUrl}/uapi/v1/activity_center/group_competition/change_group_name`, params);
};

export const createTeam = async <T>(params: CreateTeamParams) => {
  return axios.post<T>(`${baseUrl}/uapi/v1/activity_center/group_competition/team/create`, params);
};

export const joinTeam = async <T>(params: JoinTeamParams) => {
  return axios.post<T>(`${baseUrl}/uapi/v1/activity_center/group_competition/join_group`, params);
};

export const searchTeamRank = patchRequest<SearchTeamRankParams>(
  'uapi/v1/activity_center/group_competition/team/rank/search'
);

export const getAllLotteryNumber = patchRequest<LotteryPagingParams>('uapi/v1/activity_center/random/my_number', 'get');
export const getLastLotteryNumber = patchRequest<LotteryPagingParams>(
  'uapi/v1/activity_center/random/my_show_number',
  'get'
);
export const getLotteryTasks = patchRequest<ActivityId>('uapi/v1/activity_center/random/task_detail');
export const getLotteryRank = patchRequest<LotteryPagingParams>('uapi/v1/activity_center/random/rank');
export const drawLottery = patchRequest<ActivityId>('uapi/v1/activity_center/random/draw');
export const getRebateRank = patchRequest<DetailParams>('uapi/v1/activity_center/rebate/rank');

export const getUserKycLevel = patchRequest('uapi/v1/uc/kyc/user_max_level', 'get');

export const getKolActivityState = async <T>(config?: object) => {
  return axios.get<T>(`${baseUrl}/uapi/v1/activity_center/kol_whitelist/state`, {}, config);
};

export const startKolActivity = async <T>(params = {}) => {
  return axios.post<T>(`${baseUrl}/uapi/v1/activity_center/kol_whitelist/start`, params);
};
